import { Building2, User } from 'lucide-react';
import { Controller } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { BenefitType } from '@/services/types/coverage-types';
import { DocumentFieldMapping } from '@/services/types/letters-types';

import { EnhancedSelect } from '../shared/EnhancedSelect';
import type { BaseFieldProps, LoRScalarChoice } from '../types';

// Benefit type display name mapping
const BENEFIT_TYPE_LABELS: Record<BenefitType, string> = {
  [BenefitType.BODILY_INJURY_LIABILITY]: 'BI',
  [BenefitType.MEDICAL_PAYMENTS]: 'MedPay',
  [BenefitType.PERSONAL_INJURY_PROTECTION]: 'PIP',
  [BenefitType.UNINSURED_MOTORIST]: 'UM',
  [BenefitType.UNDER_INSURED_MOTORIST]: 'UIM',
  [BenefitType.PROPERTY_DAMAGE_LIABILITY]: 'PD',
  [BenefitType.COMPREHENSIVE_AUTO]: 'Comp',
  [BenefitType.COLLISION_AUTO]: 'Coll',
  [BenefitType.WORKERS_COMPENSATION]: 'WC',
  [BenefitType.OTHER]: 'Other',
} as const;

const getBenefitTypeLabel = (benefitType: string): string => {
  return BENEFIT_TYPE_LABELS[benefitType as BenefitType] || benefitType;
};

/**
 * Custom trigger content for adjuster display
 */
const renderAdjusterTrigger = (value: LoRScalarChoice | string) => {
  if (typeof value === 'string') {
    return (
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-gray-500" />
        <span className="font-medium text-gray-900">{value}</span>
      </div>
    );
  }

  const { value: displayValue, context } = value;
  return (
    <div className="flex items-center gap-2">
      <User className="h-4 w-4 text-gray-500" />
      <div className="flex flex-col items-start">
        <span className="font-medium text-gray-900 leading-tight">
          {displayValue}
        </span>
        {context?.carrier?.name && (
          <span className="text-sm text-gray-500 leading-tight">
            {context.carrier.name}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Custom choice content for adjuster display with metadata
 */
const renderAdjusterChoice = (choice: LoRScalarChoice) => {
  const { value, context } = choice;

  return (
    <div className="flex flex-col items-start w-full">
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-gray-500" />
        <span className="font-semibold text-gray-900">{value}</span>
      </div>

      {(context?.carrier?.name || context?.benefit_type) && (
        <div className="flex items-center gap-3 mt-1.5">
          {context.carrier?.name && (
            <div className="flex items-center gap-1.5">
              <Building2 className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600 font-medium">
                {context.carrier.name}
              </span>
            </div>
          )}
          {context.benefit_type && (
            <Badge
              variant="outline"
              className="text-xs px-2 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
            >
              {getBenefitTypeLabel(context.benefit_type)}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

interface AdjusterNameFieldProps
  extends BaseFieldProps<typeof DocumentFieldMapping.ADJUSTER_NAME> {
  compact?: boolean;
}

/**
 * Simplified AdjusterNameField using shared EnhancedSelect
 */
export const AdjusterNameField = ({
  field,
  control,
  name,
  required,
  error,
  compact = false,
}: AdjusterNameFieldProps) => {
  const choices = (field.default_value as LoRScalarChoice[]) || [];

  return (
    <div className={compact ? 'max-w-sm' : ''}>
      <Controller
        control={control}
        name={name}
        rules={{
          required: required
            ? 'Please select or enter an adjuster name'
            : false,
        }}
        render={({ field: ctrlField }) => (
          <EnhancedSelect
            value={ctrlField.value}
            onChange={ctrlField.onChange}
            onBlur={ctrlField.onBlur}
            name={ctrlField.name}
            choices={choices}
            placeholder="Choose an adjuster..."
            fieldDisplayName="adjuster name"
            error={error}
            renderTriggerContent={renderAdjusterTrigger}
            renderChoiceContent={renderAdjusterChoice}
            emptyStateMessage="No adjusters found"
            emptyStateDescription="No adjuster information is available for the selected coverage policy."
          />
        )}
      />
    </div>
  );
};
